import { deepClone } from './cloneAndPath';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** Coerce API id fields (string or number) to a trimmed string. */
export function coerceWorkerIdString(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return '';
}

/** Id sent on PATCH /profile/update-worker (matches testings `update worker information.js`). */
export function resolveWorkerRecordId(worker: Record<string, unknown>): string {
  for (const key of ['worker_id', 'id', 'user_id']) {
    const id = coerceWorkerIdString(worker[key]);
    if (id) {
      return id;
    }
  }
  return '';
}

/** Id for POST /profile/delist-worker — prefer explicit worker_id, then profile id, then user id. */
export function resolveWorkerDelistId(worker: Record<string, unknown>): string {
  for (const key of ['worker_id', 'id', 'user_id']) {
    const id = coerceWorkerIdString(worker[key]);
    if (id) {
      return id;
    }
  }
  return '';
}

export function workerRecordToUpdateBody(worker: Record<string, unknown>): Record<string, unknown> {
  const body: Record<string, unknown> = {
    worker_id: resolveWorkerRecordId(worker),
    email: worker.email ?? '',
    first_name: worker.first_name ?? '',
    last_name: worker.last_name ?? '',
  };
  if (isPlainObject(worker.address)) {
    body.address = deepClone(worker.address);
  } else {
    body.address = { city: '', country: '', line1: '', line2: '', postalCode: '' };
  }
  return body;
}

/** Human-readable status from typical worker profile payloads. */
export function workerStatusLabel(worker: Record<string, unknown>): string | null {
  const raw = worker.status ?? worker.worker_status ?? worker.employment_status;
  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim();
  }
  if (worker.is_active === false || worker.active === false) {
    return 'inactive';
  }
  if (worker.is_active === true || worker.active === true) {
    return 'active';
  }
  return null;
}

export function isInactiveWorker(worker: Record<string, unknown>): boolean {
  const label = workerStatusLabel(worker);
  if (!label) {
    return false;
  }
  return /inactive|delist|removed|disabled|deactivated/i.test(label);
}

/** Hide delisted / inactive workers from the list UI (GET /profile/workers often returns all). */
export function filterWorkersPayloadByActive(data: unknown, includeInactive: boolean): unknown {
  if (includeInactive) {
    return data;
  }
  const drop = (rows: unknown[]) =>
    rows.filter((row) => !isPlainObject(row) || !isInactiveWorker(row));

  if (Array.isArray(data)) {
    return drop(data);
  }
  if (isPlainObject(data)) {
    for (const key of ['workers', 'data', 'items', 'result', 'profiles']) {
      const nested = data[key];
      if (Array.isArray(nested)) {
        return { ...data, [key]: drop(nested) };
      }
    }
  }
  return data;
}

export function extractProfileRecordList(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) {
    return data.filter(isPlainObject);
  }
  if (isPlainObject(data)) {
    for (const key of ['workers', 'data', 'items', 'result', 'profiles']) {
      const nested = data[key];
      if (Array.isArray(nested)) {
        return nested.filter(isPlainObject);
      }
    }
    return [data];
  }
  return [];
}

function workerMatchesId(worker: Record<string, unknown>, workerId: string): boolean {
  const target = workerId.trim();
  if (!target) {
    return false;
  }
  return (
    resolveWorkerDelistId(worker) === target ||
    resolveWorkerRecordId(worker) === target ||
    coerceWorkerIdString(worker.worker_id) === target ||
    coerceWorkerIdString(worker.id) === target ||
    coerceWorkerIdString(worker.user_id) === target
  );
}

/** Remove one worker from list payloads returned by GET /profile/workers. */
export function removeWorkerFromWorkersPayload(data: unknown, workerId: string): unknown {
  const id = workerId.trim();
  if (!id) {
    return data;
  }

  if (Array.isArray(data)) {
    return data.filter((row) => !isPlainObject(row) || !workerMatchesId(row, id));
  }

  if (isPlainObject(data)) {
    for (const key of ['workers', 'data', 'items', 'result', 'profiles']) {
      const nested = data[key];
      if (Array.isArray(nested)) {
        return {
          ...data,
          [key]: nested.filter((row) => !isPlainObject(row) || !workerMatchesId(row, id)),
        };
      }
    }
    if (workerMatchesId(data, id)) {
      return { removed: true, worker_id: id };
    }
  }

  return data;
}

/** Nest-style error payloads sometimes arrive with HTTP 2xx; treat as failure. */
export function assertProfileMutationSuccess(data: unknown, actionLabel: string): void {
  if (!isPlainObject(data)) {
    return;
  }

  const statusCode = data.statusCode;
  if (typeof statusCode === 'number' && statusCode >= 400) {
    throw new Error(formatProfileMutationMessage(data, actionLabel));
  }

  if (data.success === false) {
    throw new Error(formatProfileMutationMessage(data, actionLabel));
  }

  const error = data.error;
  if (typeof error === 'string' && error.trim() && data.message == null && statusCode == null) {
    throw new Error(error.trim());
  }
}

function formatProfileMutationMessage(data: Record<string, unknown>, actionLabel: string): string {
  const message = data.message;
  if (Array.isArray(message)) {
    const parts = message.filter((m): m is string => typeof m === 'string' && m.trim().length > 0);
    if (parts.length > 0) {
      return parts.join(', ');
    }
  }
  if (typeof message === 'string' && message.trim()) {
    return message.trim();
  }
  if (typeof data.error === 'string' && data.error.trim()) {
    return data.error.trim();
  }
  return `${actionLabel} failed.`;
}

export function profileMutationSuccessMessage(data: unknown, fallback: string): string {
  if (!isPlainObject(data)) {
    return fallback;
  }
  const message = data.message;
  if (typeof message === 'string' && message.trim()) {
    return message.trim();
  }
  if (Array.isArray(message)) {
    const parts = message.filter((m): m is string => typeof m === 'string' && m.trim().length > 0);
    if (parts.length > 0) {
      return parts.join(', ');
    }
  }
  return fallback;
}
